import styled from 'styled-components';
import { Button } from '@/components/UI/Button/Button';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

export const PendingInvoiceBox = styled.div`
  grid-column: 1 / -1;
  margin-bottom: 10px;
`;

export const Grid = styled.section<{
  $isActiveShop: boolean;
  $isDesktop: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $isActiveShop, $isDesktop }) => {
    if ($isDesktop) return 'repeat(3, minmax(0, 1fr))';
    if ($isActiveShop) return 'repeat(2, minmax(0, 1fr))';
    return '1fr';
  }};
  gap: 10px;
  margin-bottom: ${({ $isDesktop }) => ($isDesktop ? '10px' : 0)};
  padding: ${({ $isDesktop }) => ($isDesktop ? '30px' : 0)};
  border-radius: ${({ $isDesktop }) => ($isDesktop ? '21px' : 0)};
  background-color: ${({ $isDesktop, theme }) =>
    $isDesktop ? theme.palette.primary.background : 'transparent'};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 0;
  }
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 150px;
  padding: 20px;
  border-radius: 24px;
  background-color: ${({ theme }) => theme.palette.secondary.background};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 120px;
    padding: 15px;
    border-radius: 20px;
  }
`;

export const PackageCardSlot = styled.div`
  order: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 3;
    grid-column: 1 / -1;
  }
`;

export const AccountCard = styled(Card)<{ $isActiveShop: boolean }>`
  order: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 1;

    ${({ $isActiveShop }) =>
      !$isActiveShop &&
      `
        display: grid;
        grid-template-columns: minmax(0, 1fr) 135px;
        grid-template-rows: auto auto;
        column-gap: 10px;
        min-height: 72px;
      `}
  }
`;

export const AdsBalanceCard = styled(Card)`
  order: 3;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 2;
  }
`;

export const Header = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 18px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: ${({ $isCompact }) =>
      $isCompact ? 'flex-start' : 'space-between'};
    gap: 2px;
  }
`;

export const DetailsButton = styled(Button).attrs({
  appearance: 'transparent',
  shape: 'square',
  height: 24,
  hasPadding: false,
})`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.palette.primary.labels};
`;

export const Label = styled(Typography.Body)`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.secondary.labels};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const Amount = styled(Typography.Numbers)<{ $isCompact?: boolean }>`
  margin-top: 7px;
  font-size: 24px;
  font-weight: 500;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: 4px;
    font-size: 18px;

    ${({ $isCompact }) =>
      $isCompact &&
      `
        grid-column: 1;
        grid-row: 2;
      `}
  }
`;

export const ActionButton = styled(Button).attrs({
  appearance: 'secondary',
  isFullWidth: true,
  borderRadius: 14,
})<{ $isCompact?: boolean }>`
  height: 44px;
  margin-top: auto;
  font-size: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: 10px;
    font-size: 15px;

    ${({ $isCompact }) =>
      $isCompact &&
      `
        grid-column: 2;
        grid-row: 1 / -1;
        height: 40px;
        margin-top: 0;
      `}
  }
`;

export const Description = styled(Typography.Body)`
  margin-top: auto;
  color: ${({ theme }) => theme.palette.secondary.labels};
  font-size: 13px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 12px;
  }
`;

export const BalanceSkeleton = styled(SkeletonLoader).attrs({
  appearance: 'primary',
  width: 80,
  height: 24,
  borderRadius: 7,
})<{
  $isCompact?: boolean;
}>`
  margin-top: 7px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: 4px;
    height: 22px;

    ${({ $isCompact }) =>
      $isCompact &&
      `
        grid-column: 1;
        grid-row: 2;
      `}
  }
`;
